import React, { memo, useState, useCallback, useEffect, Fragment } from 'react'
import SettingsPanel from '../../components/settings/SettingsPanel'
import {
  getStripeCustomerID,
  setSubscriptionEndedDetails,
  resetStripeCustomer
} from '../../data/stripe/customer'
import SplitForm from '../../components/stripe/CheckoutForm'
import createBillingPortalSession from '../../data/stripe/portal'
import {
  cancelSubscription,
  createSubscription,
  getCurrentSubscription
} from '../../data/stripe/subscription'
import { getCurrentPrices } from '../../data/stripe/price'
import Popup from '../../components/Popup'
import { Button } from '@material-ui/core'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { useStripe } from '@stripe/react-stripe-js'
import { getEnvironment, isProduction } from '../../data/util/environment'
import LOG from '../../logging/Logger'
import SubscriptionLevels from '../../components/settings/SubscriptionLevels'
import SubscriptionPlans from '../../components/settings/SubscriptionPlans'
import SubscriptionPlanCurrent from '../../components/settings/SubscriptionPlanCurrent'
import { useMountEffect } from '../../data/hooks'

const SettingsBillingSubscription = memo(
  ({ getUnfilteredTodos, getAreas, getUnfilteredProjects, isOffline }) => {
    const stripe = useStripe()

    const [subscriptionPrice, setSubscriptionPrice] = useState(null)
    const [subEndDate, setSubEndDate] = useState('')
    const [prices, setPrices] = useState([
      { id: '345', priceCost: '$39 / year' },
      { id: '123', priceCost: '$4 / month' }
    ])

    const [showPaymentUi, setShowPaymentUi] = useState(false)
    const [showSubscribeUi, setShowSubscribeUi] = useState(true)

    const [showPopupMsg, setShowPopupMsg] = useState(false)
    const [popupMsg, setPopupMsg] = useState(
      'There was an error with your payment details'
    )
    const [popupStyle, setPopupStyle] = useState('error')

    const retryInvoiceWithNewPaymentMethod = useCallback(
      ({ customerId, paymentMethodId, invoiceId, priceId }) => {},
      []
    )

    const handleSuccessfulSubscription = useCallback(async result => {
      LOG.info('handleSuccessfulSubscription', result)

      if (result.subscription.status === 'active') {
        setShowPaymentUi(false)
        setShowSubscribeUi(false)
        setPopupMsg('Subscription setup successfully')
        setPopupStyle('success')
        setShowPopupMsg(true)
      }
    }, [])

    const handleSuccessfulCancellation = useCallback(async result => {
      LOG.info('handleSuccessfulCancellation', result)

      if (result.deletedSubscription.status === 'canceled') {
        setSubscriptionEndedDetails(
          result.deletedSubscription.current_period_end
        )
        setShowSubscribeUi(true)
        setPopupMsg('Subscription cancelled successfully')
        setPopupStyle('success')
        setShowPopupMsg(true)
      }
    }, [])

    const handlePopupClose = useCallback((event, reason) => {
      if (reason === 'clickaway') {
        return
      }

      setShowPopupMsg(false)
    }, [])

    const handleSubscriptionError = useCallback(error => {
      LOG.error('handleSubscriptionError:', error)

      LOG.error('error.error:', error.error)
      LOG.error('error.message:', error.message)
      setPopupMsg(error.error || error.message)
      setPopupStyle('error')
      setShowPopupMsg(true)
    }, [])

    const subscribe = useCallback(async plan => {
      LOG.info('Subscribe plan:', plan)
      setSubscriptionPrice(plan)
      setShowPaymentUi(true)
    }, [])

    const cancelSub = useCallback(async () => {
      const currentSubscription = await getCurrentSubscription()
      await cancelSubscription(
        currentSubscription.subscriptionId,
        handleSuccessfulCancellation,
        handleSubscriptionError
      )
    }, [handleSuccessfulCancellation, handleSubscriptionError])

    const resetCustomer = useCallback(async () => {
      resetStripeCustomer()
    }, [])

    const handleFunction = useCallback(
      async paymentDetails => {
        const customerId = await getStripeCustomerID(handleSubscriptionError)

        // If a previous payment was attempted, get the latest invoice
        const latestInvoicePaymentIntentStatus = localStorage.getItem(
          'latestInvoicePaymentIntentStatus'
        )

        LOG.debug('[PaymentMethod]', paymentDetails)
        const paymentMethodId = paymentDetails.id
        if (latestInvoicePaymentIntentStatus === 'requires_payment_method') {
          // Update the payment method and retry invoice payment
          const invoiceId = localStorage.getItem('latestInvoiceId')
          retryInvoiceWithNewPaymentMethod({
            customerId,
            paymentMethodId,
            invoiceId,
            subscription: subscriptionPrice.id
          })
        } else {
          // Create the subscription
          createSubscription(
            stripe,
            {
              customerId,
              paymentMethodId,
              priceId: subscriptionPrice.id
            },
            handleSuccessfulSubscription,
            handleSubscriptionError
          )
        }
      },
      [
        handleSubscriptionError,
        handleSuccessfulSubscription,
        retryInvoiceWithNewPaymentMethod,
        stripe,
        subscriptionPrice
      ]
    )

    const getSubscription = useCallback(async () => {
      const currentSubscription = await getCurrentSubscription()
      setShowSubscribeUi(!currentSubscription.subscriptionId)

      if (currentSubscription.subEndDate) {
        const subscriptionEndDate = currentSubscription.subEndDate
          .replace(/T/, ' ') // replace T with a space
          .replace(/\..+/, '') // delete the dot and everything after
        setSubEndDate(subscriptionEndDate)
      }
    }, [])

    const getPrices = useCallback(async () => {
      const priceList = await getCurrentPrices(handleSubscriptionError)
      setPrices(priceList)
    }, [handleSubscriptionError])

    const openBillingPortal = useCallback(async () => {
      const url = await createBillingPortalSession(handleSubscriptionError)
      LOG.info(url)
      if (url) {
        window.location = url
      }
    }, [handleSubscriptionError])

    useMountEffect(() => {
      getPrices()
    }, [])

    useEffect(() => {
      getSubscription()
    }, [getSubscription])

    return (
      <div>
        <SettingsPanel
          title={showSubscribeUi ? 'Choose your Plan' : 'Current Subscription'}
        >
          <Grid container spacing={3}>
            {showSubscribeUi && (
              <Fragment>
                <Grid item>
                  <SubscriptionLevels
                    getUnfilteredTodos={getUnfilteredTodos}
                    getAreas={getAreas}
                    getUnfilteredProjects={getUnfilteredProjects}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SubscriptionPlans
                    prices={prices}
                    subscribe={subscribe}
                    isOffline={isOffline}
                  />
                </Grid>
              </Fragment>
            )}
            {!showSubscribeUi && (
              <Grid item xs={12}>
                <SubscriptionPlanCurrent
                  planName='Single User Plan'
                  endDate={subEndDate}
                  cancel={cancelSub}
                  isOffline={isOffline}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                onClick={openBillingPortal}
                disabled={isOffline}
              >
                Manage billing
              </Button>
            </Grid>
            {!isProduction() && (
              <Fragment>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={resetCustomer}
                  >
                    Reset stripe details
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='button'>
                    ENV: {getEnvironment()}
                  </Typography>
                </Grid>
              </Fragment>
            )}
          </Grid>
          <br />
        </SettingsPanel>
        <SplitForm
          handler={handleFunction}
          open={showPaymentUi}
          setOpen={setShowPaymentUi}
          period={subscriptionPrice ? subscriptionPrice.period : null}
        />
        <Popup
          open={showPopupMsg}
          message={popupMsg}
          onClose={handlePopupClose}
          variant={popupStyle}
        />
      </div>
    )
  }
)

export default SettingsBillingSubscription
